/**
 * `Select` (W1-12 ui-kit-i18n; contract SelectProps): Radix select (listbox popup). The trigger is a combobox
 * labelled by `label`; Enter / Space / ArrowDown open it, arrows and typeahead move, Enter picks, Escape closes.
 * 'mixed' shows "Mixed" until a value is picked. Options can carry an icon and a disabled reason (shown under the
 * option, never selectable).
 * W3-01: the options are rendered only while the list is open (UX-021: a 200-line picker no longer renders 200
 * items with the panel), the trigger draws the chosen option itself, and a value too long for the trigger ends in
 * an ellipsis with the whole text in its tooltip (UX-020).
 */

import * as RS from '@radix-ui/react-select';
import { useId, useState } from 'react';
import { useT } from '../i18n/useT';
import { useTr } from './bridge';
import { Icon } from './Icon';
import { cx } from './internal/cx';
import f from './internal/form.module.css';
import s from './Select.module.css';
import type { SelectProps } from './types';
import { m } from './ui.messages';

export interface SelectExtraProps {
  /** Accessible name when there is no visible label. */
  readonly ariaLabel?: string | undefined;
  readonly className?: string | undefined;
  readonly disabled?: boolean | undefined;
}

export function Select<T extends string>(props: SelectProps<T> & SelectExtraProps) {
  const t = useT();
  const tr = useTr();
  const labelId = useId();
  const options = props.options;
  const hasLabel = props.label !== undefined && props.label !== null;
  const mixed = props.value === 'mixed';
  const [open, setOpen] = useState(false);
  const chosen = mixed ? undefined : options.find((o) => o.value === props.value);
  const title = typeof chosen?.label === 'string' ? chosen.label : undefined;
  return (
    <div className={cx(f.control, props.className)} data-help-id={props.helpId}>
      {hasLabel && (
        <span id={labelId} className={f.label}>
          {props.label}
        </span>
      )}
      <RS.Root
        value={mixed ? '' : props.value}
        open={open}
        onOpenChange={setOpen}
        disabled={props.disabled === true}
        onValueChange={(v) => {
          const option = options.find((o) => o.value === v);
          if (option !== undefined && option.disabledReason === undefined) props.onChange(option.value);
        }}
      >
        <RS.Trigger
          className={s.trigger}
          aria-labelledby={hasLabel ? labelId : undefined}
          aria-label={hasLabel ? undefined : props.ariaLabel}
          data-mixed={mixed ? 'true' : undefined}
          title={title}
        >
          <span className={s.value}>
            <RS.Value placeholder={t(m.mixed)}>
              {chosen === undefined ? undefined : (
                <>
                  {chosen.icon !== undefined && <Icon name={chosen.icon} size={14} />}
                  {chosen.label}
                </>
              )}
            </RS.Value>
          </span>
          <RS.Icon className={s.chevron}>
            <Icon name="ChevronDown" size={14} />
          </RS.Icon>
        </RS.Trigger>
        {open && (
          <RS.Portal>
            <RS.Content className={s.content} position="popper" sideOffset={4} collisionPadding={8}>
              <RS.Viewport className={s.viewport}>
                {options.map((o) => {
                  const reason = o.disabledReason === undefined ? null : tr(o.disabledReason);
                  return (
                    <RS.Item key={o.value} value={o.value} disabled={reason !== null} className={s.item}>
                      <span className={s.check}>
                        <RS.ItemIndicator>
                          <Icon name="Check" size={14} />
                        </RS.ItemIndicator>
                      </span>
                      <span className={s.itemBody}>
                        <RS.ItemText>
                          <span className={s.itemText}>
                            {o.icon !== undefined && <Icon name={o.icon} size={14} />}
                            {o.label}
                          </span>
                        </RS.ItemText>
                        {reason !== null && <span className={f.reason}>{reason}</span>}
                      </span>
                    </RS.Item>
                  );
                })}
              </RS.Viewport>
            </RS.Content>
          </RS.Portal>
        )}
      </RS.Root>
    </div>
  );
}
